import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Box, Paper, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import { MemoPage } from './PdfPage';

const messages = defineMessages({
  group: {
    id: 'autoCuration.pdfPreview.group',
    defaultMessage: 'Group {num}',
  },
  page: {
    id: 'autoCuration.pdfPreview.page',
    defaultMessage: 'Page {num}',
  },
});

interface PdfGridSquareProps {
  pageNumber: number;
  isCurrentPage: boolean;
  isSelected: boolean;
  isGrouped: boolean;
  groupNumber: number;
  positionInGroup: number;
  groupSize: number;
  groupColor: string;
  groupBorderColor: string;
  onClick?: (pageNumber: number, e: React.MouseEvent) => void;
  onRemovePage?: (pageNumber: number) => void;
}

export const PdfGridSquare: React.FC<PdfGridSquareProps> = ({
  pageNumber,
  isCurrentPage,
  isSelected,
  isGrouped,
  groupNumber,
  positionInGroup,
  groupSize,
  groupColor,
  groupBorderColor,
  onRemovePage,
  onClick,
}) => {
  const { formatMessage } = useIntl();
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
  } = useDraggable({
    id: pageNumber,
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: pageNumber,
  });

  return (
    <div
      ref={(node) => {
        setDragRef(node);
        setDropRef(node);
      }}
      {...listeners}
      {...attributes}
      style={{
        background: isOver ? '#e3f2fd' : undefined,
        cursor: 'grab',
      }}
    >
      <Paper
        key={`page_${pageNumber}`}
        elevation={isCurrentPage ? 4 : 1}
        onClick={(e) => onClick?.(pageNumber, e)}
        sx={{
          position: 'relative',
          cursor: isGrouped ? 'move' : 'pointer',
          transition: 'all 0.2s',
          border: isSelected || isCurrentPage ? '3px solid' : '2px solid',
          borderColor:
            isSelected || isCurrentPage
              ? 'primary.main'
              : isGrouped
                ? groupBorderColor
                : 'grey.300',
          borderRadius: '8px',
          overflow: 'hidden',
          width: '100%',
          maxWidth: '200px',
          bgcolor: isSelected ? 'rgba(25, 118, 210, 0.1)' : groupColor,
          opacity: 1,
          transform: 'scale(1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
            borderColor: 'primary.main',
          },
        }}
      >
        {isSelected && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
            }}
          >
            <Icon
              name="circle-check"
              type="fas"
              color="primary.main"
              style={{
                fontSize: 32,
                filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))',
              }}
            />
          </Box>
        )}
        {isGrouped && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              borderRadius: '12px',
              px: 1,
              py: 0.25,
              fontSize: '0.7rem',
              fontWeight: 600,
              zIndex: 1,
            }}
          >
            {formatMessage(messages.group, {
              num: groupNumber,
            })}
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 1,
          }}
        >
          <MemoPage
            pageNumber={pageNumber}
            width={153}
            height={198}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: isCurrentPage ? 600 : 400,
                color: isCurrentPage ? 'primary.main' : 'text.secondary',
              }}
            >
              {formatMessage(messages.page, {
                num: pageNumber,
              })}
              {isGrouped && positionInGroup > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontSize: '0.65rem',
                    color: 'text.disabled',
                  }}
                >
                  ({positionInGroup}/{groupSize})
                </Box>
              )}
            </Typography>
            {isCurrentPage && !isGrouped && onRemovePage && (
              <Icon
                name="trash"
                type="fas"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemovePage(pageNumber);
                }}
                color="error.main"
                style={{
                  fontSize: 14,
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>
    </div>
  );
};
